import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ContentCopy,
  CheckCircle,
} from "@mui/icons-material";

const JsonModal = ({ open, onClose, data, title = "JSON Data" }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [copied, setCopied] = React.useState(false);

  const jsonString = React.useMemo(() => {
    if (!data) return "";
    try {
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return String(data);
    }
  }, [data]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {title}
        <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
          <IconButton onClick={handleCopy} size="small">
            {copied ? (
              <CheckCircle fontSize="small" color="success" />
            ) : (
              <ContentCopy fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Box
          component="pre"
          sx={{
            bgcolor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "grey.50",
            p: 2,
            borderRadius: 1,
            overflow: "auto",
            fontSize: "0.875rem",
            fontFamily: "monospace",
            border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
            maxHeight: "60vh",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {jsonString || "No data to display"}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

JsonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.any,
  title: PropTypes.string,
};

export default JsonModal;
